import { Button, Flex, Layout, Typography } from "antd";
import { useCallback, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useMatch } from "react-router-dom";
import CustomIcon from "../components/layouts/CustomIcon";
import { PATHS } from "../utills/paths";
import { formatLastModified } from "../components/projects/time";

const { Content, Header } = Layout;

/**
 * Chrome for the workspace-level listings and a project's own page.
 *
 * On a listing the header names it — "All Projects", "All Studies" — and the back arrow returns
 * wherever the user came from. On a project's detail page it names the project and steps back up
 * to the projects listing.
 *
 * The project's name reaches this header through outlet context rather than router state,
 * because state only exists when the user arrived by clicking a card — a refresh, a bookmark or
 * a shared link would leave the header blank, and shared links are the whole point of the Share
 * modal.
 */
export default function ProjectsLayout() {
  const { state } = useLocation();
  const detailMatch = useMatch(`${PATHS.V2.PROJECTS.LIST}/:projectId`);
  const studiesMatch = useMatch(PATHS.V2.STUDIES.LIST);
  const [header, setHeader] = useState(null);

  const isDetail = Boolean(detailMatch);

  // Identity-stable so a child can call it from an effect without re-triggering itself.
  const publishHeader = useCallback((next) => setHeader(next), []);
  const outletContext = useMemo(() => ({ setHeader: publishHeader }), [publishHeader]);

  const title = isDetail ? header?.title || "Project" : studiesMatch ? "All Studies" : "All Projects";
  const backTo = isDetail ? PATHS.V2.PROJECTS.LIST : state?.from || "/";
  const subtitle = isDetail && header?.modifiedTime ? formatLastModified(header.modifiedTime) : "";

  return (
    <Layout className='h-100'>
      <Header className='h-52px lh-1 p-8-24px'>
        <Flex gap={8} align='center' className='h-100'>
          <Link to={backTo} aria-label='Back'>
            <Button
              type='text'
              icon={<CustomIcon name='arrow_left' width={24} height={24} rootClassName='color-6C6C89' />}
            />
          </Link>
          <div>
            <Typography.Title level={5} className='m-0 font-size-14px fw-600 color-121217 inter'>
              {title}
            </Typography.Title>
            {subtitle && <Typography.Text className='font-size-12px color-6C6C89'>{subtitle}</Typography.Text>}
          </div>
        </Flex>
      </Header>
      <Content className='bg-F5F6F9 h-100' style={{ padding: "0 40px", overflow: "auto" }}>
        <Outlet context={outletContext} />
      </Content>
    </Layout>
  );
}
